import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  selectIdentitaeten,
  selectIdentitaetenStatus,
} from 'modules/identitaeten/features/identitaetenSlice';
import BackToOverview from 'core/components/BackToOverview';
import GesichertGate from 'modules/identitaeten/components/GesichertGate';
import IdentitaetForm from 'modules/identitaeten/components/IdentitaetForm';
import KopierWert from 'modules/identitaeten/components/KopierWert';

/**
 * Einstieg des Identitaeten-Moduls: durchsuchbare Liste aller Online-Identitaeten
 * mit Kopier-Buttons fuer Zugangsdaten. Nur im gesicherten Modus erreichbar.
 */
export default function IdentitaetenPage() {
  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 sm:py-12">
      <div className="mb-6">
        <BackToOverview />
      </div>
      <GesichertGate>
        <Uebersicht />
      </GesichertGate>
    </div>
  );
}

/** Prueft, ob eine Identitaet zum Suchbegriff passt (Dienst, E-Mail, Benutzername, Link). */
const passtZurSuche = (ident, begriff) =>
  [ident.dienst, ident.email, ident.benutzername, ident.link].some((feld) =>
    (feld || '').toLowerCase().includes(begriff)
  );

/** Entsperrte Ansicht: Kopf mit Suche und Anlegen-Button, darunter die Karten. */
function Uebersicht() {
  const identitaeten = useSelector(selectIdentitaeten);
  const status = useSelector(selectIdentitaetenStatus);
  const [suche, setSuche] = useState('');
  const [inBearbeitung, setInBearbeitung] = useState(null);

  const gefiltert = useMemo(() => {
    const begriff = suche.trim().toLowerCase();
    return begriff ? identitaeten.filter((i) => passtZurSuche(i, begriff)) : identitaeten;
  }, [identitaeten, suche]);

  return (
    <>
      <header className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-serif text-3xl font-semibold text-ink">🎭 Identitäten</h1>
          <p className="mt-1 text-sm text-ink-muted">
            Ende-zu-Ende verschlüsselt · eine eigene Identität je Online-Dienst.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setInBearbeitung({})}
          className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-500"
        >
          + Identität
        </button>
      </header>

      {identitaeten.length > 0 && (
        <input
          value={suche}
          onChange={(e) => setSuche(e.target.value)}
          placeholder="Suchen nach Dienst, E-Mail oder Benutzername…"
          className="mb-6 w-full rounded-lg border border-line bg-paper px-3 py-2 text-ink focus:border-brand-600 focus:outline-none"
        />
      )}

      {status === 'loading' && identitaeten.length === 0 && (
        <p className="text-ink-muted">Lade und entschlüssle…</p>
      )}

      {status !== 'loading' && identitaeten.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-line bg-paper-soft p-8 text-center text-ink-muted">
          Noch keine Identitäten erfasst. Lege für jeden Online-Account eine eigene Identität mit
          Alias-Adresse, generiertem Benutzernamen und Passwort an.
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {gefiltert.map((ident) => (
            <IdentitaetKarte key={ident.uuid} identitaet={ident} onBearbeiten={() => setInBearbeitung(ident)} />
          ))}
        </div>
      )}

      {inBearbeitung && <IdentitaetForm identitaet={inBearbeitung} onClose={() => setInBearbeitung(null)} />}
    </>
  );
}

/** Liefert eine oeffnbare URL (ergaenzt fehlendes https://). */
const externeUrl = (link) => (/^https?:\/\//i.test(link) ? link : `https://${link}`);

/** Eine Identitaets-Karte mit Link zum Dienst und Kopier-Feldern fuer die Zugangsdaten. */
function IdentitaetKarte({ identitaet, onBearbeiten }) {
  return (
    <div className="rounded-2xl border border-line bg-paper p-5">
      <div className="mb-3 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate font-serif text-lg font-semibold text-ink">{identitaet.dienst}</p>
          {identitaet.link && (
            <a
              href={externeUrl(identitaet.link)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-brand-600 hover:underline"
            >
              {identitaet.link} ↗
            </a>
          )}
        </div>
        <button
          type="button"
          onClick={onBearbeiten}
          title="Bearbeiten"
          className="shrink-0 text-ink-muted transition-colors hover:text-ink"
        >
          ✏️
        </button>
      </div>
      <div className="space-y-1.5">
        <KopierWert label="E-Mail" wert={identitaet.email} />
        <KopierWert label="Benutzer" wert={identitaet.benutzername} />
        <KopierWert label="Passwort" wert={identitaet.passwort} geheim />
      </div>
      {identitaet.notiz && <p className="mt-3 text-sm text-ink-muted">{identitaet.notiz}</p>}
    </div>
  );
}
